import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";

const LAB_API_FALLBACK = "https://diagnostic-new-stage.curebay.in/diagnostic/api/v1/es";

const joinUrl = (base, path) => {
  const safeBase = String(base || "").trim();
  const safePath = String(path || "").trim();
  if (!safeBase) return safePath;
  if (!safePath) return safeBase;
  return `${safeBase.replace(/\/+$/, "")}/${safePath.replace(/^\/+/, "")}`;
};

const getLabList = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.content)) return responseData.content;
  return [];
};

const getLabId = (lab) =>
  lab?.labId ||
  lab?.id ||
  lab?.code ||
  `${lab?.displayName || lab?.labName || "lab"}-${lab?.city || "na"}`;

const getLabName = (lab) =>
  lab?.displayName || lab?.labName || lab?.partnerName || lab?.name || "Lab Test";

const EditPrescriptionTestsTab = ({ labTestItems, setLabTestItems }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [labResults, setLabResults] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [labError, setLabError] = useState("");
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const [isManualAddOpen, setIsManualAddOpen] = useState(false);
  const [manualTestName, setManualTestName] = useState("");
  const [manualTestError, setManualTestError] = useState("");

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const suppressNextSearchRef = useRef(false);

  const selectedTests = useMemo(
    () => (Array.isArray(labTestItems) ? labTestItems : []),
    [labTestItems]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      if (inputRef.current?.contains(event.target)) return;
      setShowLabDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!showLabDropdown) return;

    const text = searchTerm.trim();
    if (text.length < 3) {
      setLabResults([]);
      setLabError("");
      return;
    }

    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoadingLabs(true);
        setLabError("");

        const baseUrl = process.env.REACT_APP_DIAGNOSTIC_API_URL || LAB_API_FALLBACK;
        const url = joinUrl(
          baseUrl,
          `partner-labs?labCategory=LAB&freeTextSearch=${encodeURIComponent(text)}`
        );

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch labs: ${response.status}`);
        }

        const responseData = await response.json();
        setLabResults(getLabList(responseData));
      } catch (error) {
        setLabError("Unable to load lab tests. Please try again.");
        setLabResults([]);
      } finally {
        setLoadingLabs(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, showLabDropdown]);

  const removeTest = (indexToRemove) => {
    if (typeof setLabTestItems !== "function") return;
    setLabTestItems((prev) =>
      (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== indexToRemove)
    );
  };

  const addTestFromResult = (result) => {
    if (!result || typeof setLabTestItems !== "function") return;

    const id = getLabId(result);
    const alreadyAdded = selectedTests.some((test) => {
      const existingId = test?.id || test?.labTestId || test?.code;
      return String(existingId || "") === String(id);
    });

    suppressNextSearchRef.current = true;

    if (!alreadyAdded) {
      setLabTestItems((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        {
          id,
          labTestId: result?.labTestId || result?.labId || result?.id || id,
          labTestName: getLabName(result),
          address: result?.addressLine || result?.address || "",
          city: result?.city || result?.location || "",
          diagTestType: result?.diagTestType || null,
          metadata: result,
        },
      ]);
    }

    setSearchTerm("");
    setLabResults([]);
    setShowLabDropdown(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const addTestManually = () => {
    const name = manualTestName.trim();
    if (!name) {
      setManualTestError("Lab test name is required");
      return;
    }
    if (typeof setLabTestItems === "function") {
      setLabTestItems((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        { id: `custom-test-${Date.now()}-${selectedTests.length}`, labTestId: "", labTestName: name },
      ]);
    }
    setManualTestName("");
    setManualTestError("");
    setIsManualAddOpen(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          Prescribed Tests:
        </h3>

        {selectedTests.length ? (
          <ul className="space-y-2">
            {selectedTests.map((test, index) => (
              <li
                key={`${test?.id || test?.labTestId || test?.testName || test?.name || "test"}-${index}`}
                className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-slate-700">
                    {test?.labTestName || test?.testName || test?.name || "Lab Test"}
                  </span>
                </div>
                <button type="button" onClick={() => removeTest(index)}>
                  <Trash2 size={14} className="text-rose-400" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No tests prescribed.</p>
        )}
      </div>

      <div className="rounded-lg border border-blue-200 bg-[#f6fbff] p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-base font-semibold text-gray-700">Add Tests</span>
          <button
            type="button"
            onClick={() => {
              setIsManualAddOpen((prev) => !prev);
              setManualTestName("");
              setManualTestError("");
              setShowLabDropdown(false);
            }}
            className="text-xs font-medium text-[#4b78c2] hover:underline"
          >
            {isManualAddOpen ? "Search instead" : "+ Add manually"}
          </button>
        </div>

        {isManualAddOpen ? (
          <div>
            <div className="mb-2 text-xs font-semibold text-gray-700">Lab Test Name</div>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                placeholder="Type lab test name"
                value={manualTestName}
                onChange={(e) => {
                  setManualTestName(e.target.value);
                  setManualTestError("");
                }}
                onKeyDown={(e) => { if (e.key === "Enter") addTestManually(); }}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={addTestManually}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            {manualTestError ? (
              <p className="mt-1 text-xs text-red-600">{manualTestError}</p>
            ) : null}
          </div>
        ) : (
          <div className="mt-2">
            <div className="mb-2 text-xs font-semibold text-gray-700">Lab Test Name</div>
            <div className="relative">
              <input
                ref={inputRef}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm"
                placeholder="Search for lab tests"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowLabDropdown(true);
                }}
                onFocus={() => {
                  setShowLabDropdown(true);
                }}
                autoComplete="off"
              />
              {loadingLabs ? (
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  ...
                </span>
              ) : null}

              {showLabDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 mt-2 w-full max-h-64 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                >
                  {searchTerm.trim().length < 3 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Type at least 3 characters.
                    </div>
                  ) : labError ? (
                    <div className="px-4 py-3 text-sm text-red-600">{labError}</div>
                  ) : loadingLabs ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Loading lab tests...
                    </div>
                  ) : labResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No lab tests found.{" "}
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (typeof setLabTestItems === "function") {
                            setLabTestItems((prev) => [
                              ...(Array.isArray(prev) ? prev : []),
                              {
                                id: `custom-test-${Date.now()}-${selectedTests.length}`,
                                labTestId: "",
                                labTestName: searchTerm.trim(),
                              },
                            ]);
                          }
                          setSearchTerm("");
                          setShowLabDropdown(false);
                        }}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        Add "{searchTerm.trim()}" manually
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {labResults.map((lab, idx) => (
                        <button
                          key={`${getLabId(lab)}-${idx}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addTestFromResult(lab);
                          }}
                          className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm hover:bg-blue-50"
                        >
                          <span className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                          <span>
                            <span className="block font-semibold text-gray-900">
                              {getLabName(lab)}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {[lab?.addressLine || lab?.address, lab?.city || lab?.location]
                                .filter(Boolean)
                                .join(" • ")}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPrescriptionTestsTab;

